import React, { useState, useRef } from "react";
import { LogOut } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import AuthButton from "../components/AuthButton";
import PintoAuthShell, {
  CARD_SUBTLE,
  CARD_TEXT,
  PINTO_LINK_CLASS,
  PRIMARY_BTN_CLASS,
} from "../components/PintoAuthShell";
import { verifyEmail, resendVerificationCode } from "../services/UserService";
import { logOut } from "../services/AuthService";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { track } from "../utils/analytics";
import { FREE_WELCOME_PATH, heldPostAuthPath } from "../utils/dealOnboarding";
import { reportOnboardingStep } from "../services/LifecycleService";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import {
  consumePendingAuthIntent,
  getAuthReturnRetailer,
  persistPendingAuthReturn,
  sanitizeAuthSource,
  sanitizeInternalReturnTo,
} from "../utils/authReturn";

const OTP_CLASS =
  "w-11 h-14 sm:w-12 sm:h-14 text-center text-2xl font-extrabold text-[#1e1b3a] rounded-2xl border-[1.5px] border-[rgba(124,58,237,0.18)] bg-[rgba(124,58,237,0.06)] focus:bg-white focus:border-[#7c3aed] focus:ring-4 focus:ring-[#7c3aed]/15 outline-none transition-all";

export default function VerificationPage() {
  useEnforceLightMode();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { user } = useUserContext();
  const [searchParams] = useSearchParams();
  const queryReturnTo = sanitizeInternalReturnTo(searchParams.get("returnTo"));
  const intentSource = sanitizeAuthSource(searchParams.get("source"));
  const verifiedDestination = useRef<string | undefined>(undefined);
  const verifiedUser = { ...user, verified: true as const };
  const fallbackDestination = user?.retailers?.length
    ? "/setup"
    : heldPostAuthPath(verifiedUser) || FREE_WELCOME_PATH;

  React.useEffect(() => {
    persistPendingAuthReturn(queryReturnTo, intentSource);
  }, [intentSource, queryReturnTo]);

  React.useEffect(() => {
    if (user && !user.verified) reportOnboardingStep("verify");
  }, [user]);

  const resumeIntent = () => {
    const held = heldPostAuthPath(verifiedUser);
    if (held) return held;
    const intent = consumePendingAuthIntent(queryReturnTo || fallbackDestination);
    const destination = intent.returnTo;
    if (destination !== fallbackDestination) {
      track("guest_intent_resumed", {
        surface: "web",
        retailer: getAuthReturnRetailer(destination),
        trigger: "verification",
        source: intent.source || intentSource || "guest_signup",
      });
    }
    return destination;
  };

  const applyDigits = (raw: string, start = 0) => {
    const digits = raw.replace(/\D/g, "").slice(0, 6 - start);
    setVerificationCode((prev) => {
      const next = [...prev];
      if (!digits) {
        next[start] = "";
        return next;
      }
    digits.split("").forEach((digit, i) => {
      next[start + i] = digit;
    });
    return next;
  });
  inputRefs.current[digits ? Math.min(start + digits.length, 5) : start]?.focus();
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = verificationCode.join("");
    if (code.length !== 6) return;

    setIsVerifying(true);
    setError("");
    track("verification-attempted", { userId: user?.userId });

    try {
      await verifyEmail(code);
      track("verification-successful", { userId: user?.userId });
      window.location.href = resumeIntent();
    } catch (err) {
      track("verification-error", {
        userId: user?.userId,
        errorMessage: err instanceof Error ? err.message : "Unknown error",
      });
      setError(err instanceof Error ? err.message : "Verification failed");
      setIsVerifying(false);
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    if (value.length > 1) {
      applyDigits(value, index);
      return;
    }
    if (value && !/^\d$/.test(value)) return;
    applyDigits(value, index);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    applyDigits(e.clipboardData.getData("text"));
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setResendMessage(null);
    setError("");
    try {
      await resendVerificationCode();
      setResendMessage(t("auth.resendSuccess"));
    } catch (err) {
      setResendMessage(t("auth.resendError"));
      console.error("Resend verification code failed:", err);
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = () => {
    queryClient.clear();
    logOut();
  };

  if (user?.verified) {
    verifiedDestination.current ||= resumeIntent();
    return <Navigate to={verifiedDestination.current} replace />;
  }

  const codeComplete = verificationCode.join("").length === 6;

  return (
    <PintoAuthShell
      topRight={
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-2 px-3.5 h-9 rounded-full bg-white/12 border border-white/22 text-white text-sm font-bold hover:bg-white/18 transition-colors"
        >
          <LogOut size={15} />
          <span className="hidden sm:inline">{t("common.logout")}</span>
        </button>
      }
      footer={
        <p className="text-[12px] text-white/85 font-semibold drop-shadow-[0_1px_4px_rgba(30,27,58,0.4)]">
          {t("auth.needHelp")}{" "}
          <a
            href="mailto:support@dealhawk.com"
            className="font-extrabold text-white hover:text-amber-200 transition-colors"
          >
            {t("auth.contactSupport")}
          </a>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-1.5">
          <h1
            className={`text-[26px] sm:text-3xl font-extrabold tracking-[-0.02em] leading-tight ${CARD_TEXT}`}
          >
            {t("auth.checkYourInbox")}
          </h1>
          <p className={`text-sm font-medium leading-relaxed ${CARD_SUBTLE}`}>
            {t("auth.weSentACodeTo")}{" "}
            <strong className={CARD_TEXT}>{user?.userId}</strong>
          </p>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-2xl text-[13px] font-medium bg-red-50 border border-red-200/70 text-red-700">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-center">
          {verificationCode.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={index === 0 ? 6 : 1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={OTP_CLASS}
              required
            />
          ))}
        </div>

        <AuthButton
          type="submit"
          disabled={!codeComplete || isVerifying}
          loading={isVerifying}
          className={PRIMARY_BTN_CLASS}
        >
          {t("auth.verifyEmail")}
        </AuthButton>

        <p className={`text-center text-[13px] font-medium ${CARD_SUBTLE}`}>
          {resendMessage || (
            <>
              {t("auth.didntGetIt")}{" "}
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
                className={`${PINTO_LINK_CLASS} disabled:opacity-50`}
              >
                {isResending ? t("auth.sending") : t("auth.resendCode")}
              </button>
            </>
          )}
        </p>

        <p className={`-mt-3 text-center text-[13px] font-medium ${CARD_SUBTLE}`}>
          {t("auth.wrongAccount")}{" "}
          <button type="button" onClick={handleLogout} className={PINTO_LINK_CLASS}>
            {t("auth.signOut")}
          </button>
        </p>
      </form>
    </PintoAuthShell>
  );
}
